import { IssuesTable } from "./IssuesTable";
import { AddEditIssueModal } from "./AddEditIssueModal";
import { IssueHistoryModal} from "./IssueHistoryModal";
import {
  NumberParam,
  useQueryParams,
} from "use-query-params";
import { useGetProjectIssuesQuery, useCreateProjectIssueMutation, useUpdateProjectIssueMutation, useDeleteProjectIssueMutation } from "@/services";
import { IErrorResponse, IProjectIssue, IProjectIssueFormValues, IQueryRequest } from "../interfaces";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { Button, Flex } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCirclePlus } from "@fortawesome/free-solid-svg-icons";
import { ConfirmModal } from "@/components";
import { useToggle } from "@/hooks";
import { useProjectContext } from "@/providers";
import { useIsAdmin } from "@/helpers/permissions";
import { handleServerError } from "../helpers/error-handling";

export const IssuesTab = ({ publicProjectId, onTotalCountChange }: { publicProjectId: string, onTotalCountChange: (totalCount: number | undefined) => void }) => {

  const [issuesQueryParams, setIssuesQueryParams] = useQueryParams({
    i_page: NumberParam,
  });

  const [issuesQueryRequest, setIssuesQueryRequest] = useState<IQueryRequest>();
  const intl = useIntl();
  const { isUserProjectLeader } = useProjectContext();
  const isAdmin = useIsAdmin();
  const canUpdateIssue = isAdmin || isUserProjectLeader();
  const canDeleteIssue = isAdmin || isUserProjectLeader();
  const [issueToUpdate, setIssueToUpdate] = useState<IProjectIssue | null>(null);
  const [issueHistoryToView, setIssueHistoryToView] = useState<string>();
  const [isIssueHistoryModalOpen, setIssueHistoryModalOpen] = useState(false);
  const [pendingIssueIdToDelete, setPendingIssueIdToDelete] = useState<string>();
  const [deleteConfirmationHidden, toggleDeleteConfirmation] = useToggle();
  const [deleteProjectIssue, { isLoading: isDeletingIssue }] = useDeleteProjectIssueMutation();

  const {
    data: { data: issuesData, totalCount: issuesTotalCount } = {} as {
      data?: IProjectIssue[];
      totalCount?: number;
    },
    isFetching: isFetchingIssues,
    isError: isIssuesError,
    refetch,
  } = useGetProjectIssuesQuery(
    { publicProjectId, queryParameters: issuesQueryRequest },
    { skip: !issuesQueryRequest }
  );

  useEffect(() => {
    onTotalCountChange(issuesTotalCount);
  }, [issuesTotalCount, onTotalCountChange]);

  useEffect(() => {
    const queryRequest: IQueryRequest = {
      page: issuesQueryParams.i_page,
    };
    setIssuesQueryRequest(queryRequest);
  }, [issuesQueryParams]);

  useEffect(() => {
    if (!isIssuesError) return;

    toast.error(intl.formatMessage({ id: "general.toastSomethingWentWrong" }));
  }, [intl, isIssuesError]);

  const [isAddEditIssueModalOpen, setIsAddEditIssueModalOpen] = useState(false);
  const [createProjectIssue] = useCreateProjectIssueMutation();
  const [updateProjectIssueMutation] = useUpdateProjectIssueMutation();

  function openCreateUpdateModal(projectIssue: IProjectIssue | null) {
    setIssueToUpdate(projectIssue);
    setIsAddEditIssueModalOpen(true);
  }

  function closeCreateUpdateModal() {
    setIssueToUpdate(null);
    setIsAddEditIssueModalOpen(false);
  }

  async function createNewProjectIssue(formValues: IProjectIssueFormValues): Promise<boolean> {
    try {
      await createProjectIssue({ publicProjectId, body: formValues })
        .unwrap();
      toast.success(intl.formatMessage({ id: "risksAndIssues.createIssueSuccess" }));
      return true;
    } catch (response) {
      handleServerError(response as IErrorResponse, intl);
      return false;
    }
  }

  async function updateProjectIssue({ publicProjectId, publicProjectIssueId, body }: { publicProjectId: string; publicProjectIssueId: string; body: IProjectIssueFormValues }): Promise<boolean> {
    try {
      await updateProjectIssueMutation({ publicProjectId, publicProjectIssueId, body })
        .unwrap();
      toast.success(intl.formatMessage({ id: "risksAndIssues.updateIssueSuccess" }));
      return true;
    } catch (response) {
      handleServerError(response as IErrorResponse, intl);
      return false;
    }
  }

  const handleUpdateClick = (projectIssue: IProjectIssue) => openCreateUpdateModal(projectIssue);

  const handleDeleteClick = (projectIssueId: string) => {
    if (!canDeleteIssue) {
      return;
    }

    setPendingIssueIdToDelete(projectIssueId);
    toggleDeleteConfirmation();
  };

  async function handleDeleteIssue() {
    try {
      await deleteProjectIssue({ publicProjectId, publicProjectIssueId: pendingIssueIdToDelete })
        .unwrap();
      setPendingIssueIdToDelete(undefined);
      toggleDeleteConfirmation();
      toast.success(intl.formatMessage({ id: "general.toastChangesSaved" }));
      refetch();
    } catch (response) {
      handleServerError(response as IErrorResponse, intl);
    }
  }
const handleViewHistoryClick = (projectIssueId: string) => {
    setIssueHistoryModalOpen(true);
    setIssueHistoryToView(projectIssueId);
}

const closeIssueHistoryModal = () => {
    setIssueHistoryModalOpen(false);
    setIssueHistoryToView(undefined);
}

  return (
    <Flex gap="middle" vertical>
      <Flex justify="flex-end">
        {canUpdateIssue && (
          <Button
            data-test-id="project-add-issue"
            type="primary"
            onClick={() => openCreateUpdateModal(null)}
            icon={<FontAwesomeIcon icon={faCirclePlus} />}
          >
            {intl.formatMessage({ id: "risksAndIssues.createIssue" })}
          </Button>
        )}
      </Flex>
      <AddEditIssueModal
        open={isAddEditIssueModalOpen}
        onClose={closeCreateUpdateModal}
        onOk={async (values) => {
          let isSuccess = false;
          let pageNumber;
          if (!issueToUpdate) {
            isSuccess = await createNewProjectIssue(values);
            pageNumber = 1;
          } else {
            isSuccess = await updateProjectIssue({ publicProjectId, publicProjectIssueId: issueToUpdate.projectIssuePublicId, body: values });
            pageNumber = issuesQueryParams.i_page;
          }

          if (isSuccess) {
            closeCreateUpdateModal();
            setIssuesQueryParams({ ...issuesQueryParams, i_page: pageNumber });
            refetch();
          }
        }}
        projectIssue={issueToUpdate}
      />
    <IssueHistoryModal
        open={isIssueHistoryModalOpen}
        onClose={closeIssueHistoryModal}
        projectIssueId={issueHistoryToView}
    />
      <IssuesTable
        data={issuesData}
        totalCount={issuesTotalCount}
        isFetching={isFetchingIssues}
        canUpdate={canUpdateIssue}
        onUpdate={handleUpdateClick}
        canDelete={canDeleteIssue}
        onDelete={handleDeleteClick}
        onViewHistory={handleViewHistoryClick}
      />
      <ConfirmModal
        loading={isDeletingIssue}
        onSaveChanges={handleDeleteIssue}
        hidden={deleteConfirmationHidden}
        toggle={toggleDeleteConfirmation}
        headerTitleKey="risksAndIssues.deleteIssueModalTitle"
        bodyTextKey="risksAndIssues.deleteIssueModalDescription"
        additionalBodyText={undefined}
        cancelLabel={undefined}
      />
    </Flex>
  );
};